import React from "react"

/**
 * @param {{
 *   variant?: string,
 *   children: any,
 *   className?: string,
 *   paddingX?: string | number,
 *   title?: string,
 *   style?: any,
 * }} props
 */
export default function Badge({
    variant = "default",
    children,
    className = "",
    paddingX = undefined,
    title = undefined,
    style = undefined,
}) {
    function hexToRgba(hex, alpha) {
        const raw = String(hex ?? "").replace("#", "").trim()
        if (raw.length !== 6) {
            return `rgba(0, 0, 0, ${alpha})`
        }
        const r = parseInt(raw.slice(0, 2), 16)
        const g = parseInt(raw.slice(2, 4), 16)
        const b = parseInt(raw.slice(4, 6), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    const hasCustomStyle =
        style != null && typeof style === "object" && Object.keys(style).length > 0

    function getVariantClasses() {
        // Custom inline colors: do NOT use Bootstrap `border` — it sets
        // `border: … !important` and overrides inline borderWidth/borderColor.
        if (hasCustomStyle) {
            return ""
        }
        switch (variant) {
        case "success":
            return "bg-success-subtle text-success border border-success"
        case "warning":
            return "bg-warning-subtle text-warning border border-warning"
        case "error":
            return "bg-danger-subtle text-danger border border-danger"
        case "info":
            return "bg-info-subtle text-info border border-info"
        case "frozen":
            return "text-info border border-info bg-info-subtle"
        case "boxed":
            return "text-success border border-success bg-success-subtle"
        case "transferring":
            return "border"
        case "received":
            return "border"
        case "weighed":
            return "border"
        case "partial":
            return "border"
        case "shipment":
            return "border"
        case "invoiced":
            return "border"
        default:
            return "bg-secondary-subtle text-secondary border border-secondary"
        }
    }

    function getVariantStyle() {
        // v1 colors (from `helpers.statusColor`) adapted to our subtle v2 pill style.
        // We use the v1 color as the accent (text + border) and a low-alpha fill.
        switch (variant) {
        case "transferring": {
            const c = "#3491b3"
            return { color: c, borderColor: c, backgroundColor: hexToRgba(c, 0.16) }
        }
        case "received": {
            const c = "#8d34b3"
            return { color: c, borderColor: c, backgroundColor: hexToRgba(c, 0.16) }
        }
        case "weighed": {
            const c = "#6ebaa4"
            return { color: c, borderColor: c, backgroundColor: hexToRgba(c, 0.16) }
        }
        case "partial": {
            const c = "#ef4524"
            return { color: c, borderColor: c, backgroundColor: hexToRgba(c, 0.14) }
        }
        case "shipment": {
            const c = "#474ca0"
            return { color: c, borderColor: c, backgroundColor: hexToRgba(c, 0.16) }
        }
        case "invoiced": {
            const c = "#343635"
            return { color: c, borderColor: c, backgroundColor: hexToRgba(c, 0.12) }
        }
        default:
            return {}
        }
    }

    return (
        <span
            className={
                `badge rounded-pill d-inline-flex align-items-center ${paddingX != null ? "" : "px-3 "}py-1 ` +
                `${getVariantClasses()} ${className}`
            }
            title={title}
            style={{
                fontSize: "0.75rem",
                fontWeight: 500,
                transition: "all 0.2s",
                ...(paddingX != null
                    ? { paddingLeft: paddingX, paddingRight: paddingX }
                    : {}),
                ...getVariantStyle(),
                ...(style ?? {}),
                ...(hasCustomStyle
                    ? {
                        borderStyle: "solid",
                        borderWidth: "1px",
                        boxSizing: "border-box",
                    }
                    : {}),
            }}
        >
            {children}
        </span>
    )
}
